// What happens to the OPEN AMOUNT when OCR/DocuAI data is applied onto a receipt form.
//
// The trap this exists to close: OCR reads a DOCUMENT. It can tell you the invoice total, never how
// much of it has already been paid — `ocrData` carries no `outstandingAmount`, ever. The old chain
// in receipt-edit.tsx was `outstandingAmount ?? priceTotal ?? …`, so it fell through to the document
// total on EVERY apply, and on a receipt that was already partly paid it overwrote the real open
// amount with the full total.
//
// Measured on 2026-09-16 (Ausgabe 12495): open 3622,17 of a 3822,17 total, 200,00 already paid, but
// the payment dialog offered 3822,15 (the DocuAI total). Two payments entered against that basis
// looked balanced to the client, so it sent the plain `payment` type; the server measured them
// against 3622,17, saw a difference, and `createAssignments` refused with `type: NOT_ALLOWED`. The
// green „Bezahlt" badge on a `partiallyPaid` expense was the same wrong basis, seen from the front.
//
// The rule: only a receipt with nothing paid yet may take the document total as its open amount.
// Once part of it is settled, the receipt's own open amount is the truth and OCR must not touch it.
// Prices themselves (priceTotal, priceKind) are a different question — those SHOULD follow OCR.

const toAmount = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

// Sequelize hands DECIMAL columns over as strings, so compare as numbers and allow for the cent that
// floating point loses on the way. Anything at or below half a cent apart counts as "nothing paid".
const SETTLED_EPSILON = 0.005

/**
 * Has part of this receipt already been settled? True when the receipt knows its own open amount and
 * that amount differs from its total — which is exactly what a payment does to it.
 */
export const hasSettledPart = (outstandingAmount: unknown, priceTotal: unknown): boolean => {
  const outstanding = toAmount(outstandingAmount)
  const total = toAmount(priceTotal)
  if (outstanding === null || total === null) return false
  return Math.abs(Math.abs(total) - Math.abs(outstanding)) > SETTLED_EPSILON
}

/**
 * The open amount a receipt form should carry after OCR data was applied to it.
 *
 * @param applied  the values coming out of the OCR/DocuAI apply step
 * @param current  the receipt as the form holds it today (its own open amount lives here)
 */
export const resolveAppliedOutstandingAmount = (
  applied: { outstandingAmount?: unknown, priceTotal?: unknown, priceGross?: unknown, priceNet?: unknown },
  current: { outstandingAmount?: unknown, priceTotal?: unknown },
): unknown => {
  // An explicit open amount in the applied data wins — nothing infers, it was stated.
  if (toAmount(applied.outstandingAmount) !== null) return applied.outstandingAmount

  // Something is already paid on this receipt: its open amount survives the apply untouched.
  if (hasSettledPart(current.outstandingAmount, current.priceTotal)) return current.outstandingAmount

  // Nothing paid yet, so the (possibly freshly read) total IS the open amount.
  return applied.priceTotal ?? applied.priceGross ?? applied.priceNet ?? current.outstandingAmount
}
